import { QueryClient } from '@tanstack/react-query';
import { LicenseSystemChangedChangeType, LicenseSystemChangedPayload } from './generated/types';
import { showLicenseSystemChangedToast } from './helpers/showLicenseSystemChangedToast';
import { useGetOrganizationResult } from './useGetOrganization';
import { TFunction } from 'i18next';

interface HandleOrgWideLicenseSystemChangedEventInput {
    event: LicenseSystemChangedPayload;
    orgId: string;
    queryClient: QueryClient;
    t: TFunction;
}

const updateLicenseSystemsPendingState = (
    queryClient: QueryClient,
    orgId: string,
    licenseSystemIds: Array<string>,
    hasPendingProcess: boolean,
) => {
    queryClient.setQueryData<useGetOrganizationResult>(
        ['getOrganization', orgId],
        (cachedOrganization) => {
            if (!cachedOrganization) return cachedOrganization;

            return {
                ...cachedOrganization,
                organization: {
                    ...cachedOrganization.organization,
                    licenseSystems: cachedOrganization.organization.licenseSystems.map((licenseSystem) => ({
                        ...licenseSystem,
                        hasPendingProcess: licenseSystemIds.includes(licenseSystem.id)
                            ? hasPendingProcess
                            : licenseSystem.hasPendingProcess,
                    })),
                },
            };
        },
    );
};

const getTransferLicenseSystemIds = (event: LicenseSystemChangedPayload) => [
    event.sourceLicenseSystemId,
    event.targetLicenseSystemId,
].filter((licenseSystemId): licenseSystemId is string => Boolean(licenseSystemId));

export const handleOrgWideLicenseSystemChangedEvent = ({ event, orgId, queryClient, t }: HandleOrgWideLicenseSystemChangedEventInput) => {
    switch (event.changeType) {
        case LicenseSystemChangedChangeType.TransferStarted:
            showLicenseSystemChangedToast(event, t);
            updateLicenseSystemsPendingState(queryClient, orgId, getTransferLicenseSystemIds(event), true);
            break;

        case LicenseSystemChangedChangeType.TransferAccepted:
            showLicenseSystemChangedToast(event, t);
            break;

        case LicenseSystemChangedChangeType.LicenseSystemUpdated:
            if (event.licenseSystemId) {
                updateLicenseSystemsPendingState(queryClient, orgId, [event.licenseSystemId], false);
            }
            break;

        case LicenseSystemChangedChangeType.TransferCompleted:
            showLicenseSystemChangedToast(event, t);
            updateLicenseSystemsPendingState(queryClient, orgId, getTransferLicenseSystemIds(event), false);
            break;

        case LicenseSystemChangedChangeType.TransferFailed:
            showLicenseSystemChangedToast(event, t);
            updateLicenseSystemsPendingState(queryClient, orgId, getTransferLicenseSystemIds(event), false);
            break;

        case LicenseSystemChangedChangeType.AddLicenseSystemStarted:
        case LicenseSystemChangedChangeType.AddLicenseSystemCompleted:
        case LicenseSystemChangedChangeType.AddLicenseSystemFailed:
        case LicenseSystemChangedChangeType.ReactivateLicenseSystemStarted:
        case LicenseSystemChangedChangeType.ReactivateLicenseSystemCompleted:
        case LicenseSystemChangedChangeType.ReactivateLicenseSystemFailed:
            break;
    }
};
